import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { BaseElement } from "../../utils/ui-components.js";

@customElement("api-docs-keywords")
export class ApiDocsKeywords extends BaseElement {
    @property({ type: String }) apiKey = "YOUR_API_KEY";
    @property({ type: String }) baseUrl = "https://appshovel.com";

    connectedCallback() {
        super.connectedCallback();
        this.style.display = "block";
    }

    render() {
        return html`
            <section class="space-y-6">
                <h2 class="text-2xl font-bold">Keyword Endpoints</h2>

                ${this.renderPopularities()} ${this.renderRecommendations()}
            </section>
        `;
    }

    private renderPopularities() {
        return html`
            <div class="space-y-4">
                <h3 class="text-xl font-semibold"><code class="badge badge-ghost badge-lg">POST</code> /api/v1/keywords/popularities</h3>
                <p>Fetch popularity and difficulty scores for up to 100 keywords at once.</p>

                <h4 class="font-semibold">Request Body</h4>
                <div class="overflow-x-auto">
                    <table class="table table-sm">
                        <thead>
                            <tr>
                                <th>Field</th>
                                <th>Type</th>
                                <th>Required</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>terms</code></td>
                                <td>string[]</td>
                                <td>Yes</td>
                                <td>Keywords to look up (1–100 non-empty strings)</td>
                            </tr>
                            <tr>
                                <td><code>country</code></td>
                                <td>string</td>
                                <td>Yes</td>
                                <td>Two-letter country code (see <a href="#reference" class="link link-primary">Reference</a>)</td>
                            </tr>
                            <tr>
                                <td><code>platform</code></td>
                                <td>string</td>
                                <td>Yes</td>
                                <td><code>iphone</code> or <code>ipad</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h4 class="font-semibold">Example Request</h4>
                <api-docs-code-block
                    .code=${`curl -X POST ${this.baseUrl}/api/v1/keywords/popularities \\
  -H "Authorization: Bearer ${this.apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "terms": ["weather", "fitness tracker"],
    "country": "us",
    "platform": "iphone"
  }'`}
                ></api-docs-code-block>

                <h4 class="font-semibold">Example Response</h4>
                <api-docs-code-block
                    .code=${`[
  {
    "term": "weather",
    "country": "us",
    "platform": "iphone",
    "popularity": 82,
    "difficulty": 95
  },
  {
    "term": "fitness tracker",
    "country": "us",
    "platform": "iphone",
    "popularity": 55,
    "difficulty": 61
  }
]`}
                ></api-docs-code-block>

                <h4 class="font-semibold">Response Fields</h4>
                <div class="overflow-x-auto">
                    <table class="table table-sm">
                        <thead>
                            <tr>
                                <th>Field</th>
                                <th>Type</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>term</code></td>
                                <td>string</td>
                                <td>The keyword</td>
                            </tr>
                            <tr>
                                <td><code>country</code></td>
                                <td>string</td>
                                <td>Country code</td>
                            </tr>
                            <tr>
                                <td><code>platform</code></td>
                                <td>string</td>
                                <td>Platform</td>
                            </tr>
                            <tr>
                                <td><code>popularity</code></td>
                                <td>number | null</td>
                                <td>Search popularity score (0–100), or null if unavailable</td>
                            </tr>
                            <tr>
                                <td><code>difficulty</code></td>
                                <td>number | null</td>
                                <td>Competition difficulty score (0–100), or null if unavailable</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        `;
    }

    private renderRecommendations() {
        return html`
            <div class="divider"></div>
            <div class="space-y-4">
                <h3 class="text-xl font-semibold"><code class="badge badge-ghost badge-lg">POST</code> /api/v1/keywords/recommendations</h3>
                <p>Get keyword recommendations (related keywords) for a single term.</p>

                <h4 class="font-semibold">Request Body</h4>
                <div class="overflow-x-auto">
                    <table class="table table-sm">
                        <thead>
                            <tr>
                                <th>Field</th>
                                <th>Type</th>
                                <th>Required</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>term</code></td>
                                <td>string</td>
                                <td>Yes</td>
                                <td>A single keyword to get recommendations for</td>
                            </tr>
                            <tr>
                                <td><code>country</code></td>
                                <td>string</td>
                                <td>Yes</td>
                                <td>Two-letter country code (see <a href="#reference" class="link link-primary">Reference</a>)</td>
                            </tr>
                            <tr>
                                <td><code>platform</code></td>
                                <td>string</td>
                                <td>Yes</td>
                                <td><code>iphone</code> or <code>ipad</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h4 class="font-semibold">Example Request</h4>
                <api-docs-code-block
                    .code=${`curl -X POST ${this.baseUrl}/api/v1/keywords/recommendations \\
  -H "Authorization: Bearer ${this.apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "term": "weather",
    "country": "us",
    "platform": "iphone"
  }'`}
                ></api-docs-code-block>

                <h4 class="font-semibold">Example Response</h4>
                <api-docs-code-block
                    .code=${`{
  "term": "weather",
  "country": "us",
  "platform": "iphone",
  "recommendations": [
    { "name": "weather forecast", "popularity": 70 },
    { "name": "weather radar", "popularity": 65 },
    { "name": "weather app", "popularity": 58 }
  ]
}`}
                ></api-docs-code-block>

                <h4 class="font-semibold">Response Fields</h4>
                <div class="overflow-x-auto">
                    <table class="table table-sm">
                        <thead>
                            <tr>
                                <th>Field</th>
                                <th>Type</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>term</code></td>
                                <td>string</td>
                                <td>The input keyword</td>
                            </tr>
                            <tr>
                                <td><code>country</code></td>
                                <td>string</td>
                                <td>Country code</td>
                            </tr>
                            <tr>
                                <td><code>platform</code></td>
                                <td>string</td>
                                <td>Platform</td>
                            </tr>
                            <tr>
                                <td><code>recommendations</code></td>
                                <td>object[]</td>
                                <td>Array of related keywords</td>
                            </tr>
                            <tr>
                                <td><code>recommendations[].name</code></td>
                                <td>string</td>
                                <td>Recommended keyword</td>
                            </tr>
                            <tr>
                                <td><code>recommendations[].popularity</code></td>
                                <td>number</td>
                                <td>Popularity score (0–100)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        `;
    }
}
